import { useState, useEffect } from 'react';
import { supabase } from '../supabase';

const GOLD = '#FFC220';

const emptyForm = {
  image_url: '',
  caption: '',
  instagram_url: '',
  sort_order: 1,
  active: true,
};

export default function InstagramFeed() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('instagram_feed')
      .select('*')
      .order('sort_order', { ascending: true });
    if (!error) setPosts(data);
    setLoading(false);
  };

  const handleEdit = (post) => {
    setForm({
      image_url: post.image_url || '',
      caption: post.caption || '',
      instagram_url: post.instagram_url || '',
      sort_order: post.sort_order || 1,
      active: post.active,
    });
    setEditingId(post.id);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this photo?')) return;
    await supabase.from('instagram_feed').delete().eq('id', id);
    fetchPosts();
  };

  const handleToggle = async (post) => {
    await supabase.from('instagram_feed').update({ active: !post.active }).eq('id', post.id);
    fetchPosts();
  };

  const handleMoveUp = async (post, index) => {
    if (index === 0) return;
    const above = posts[index - 1];
    await supabase.from('instagram_feed').update({ sort_order: above.sort_order }).eq('id', post.id);
    await supabase.from('instagram_feed').update({ sort_order: post.sort_order }).eq('id', above.id);
    fetchPosts();
  };

  const handleMoveDown = async (post, index) => {
    if (index === posts.length - 1) return;
    const below = posts[index + 1];
    await supabase.from('instagram_feed').update({ sort_order: below.sort_order }).eq('id', post.id);
    await supabase.from('instagram_feed').update({ sort_order: post.sort_order }).eq('id', below.id);
    fetchPosts();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.image_url) {
      alert('Please enter an image URL.');
      return;
    }
    setSaving(true);
    const payload = {
      image_url: form.image_url,
      caption: form.caption || null,
      instagram_url: form.instagram_url || null,
      sort_order: parseInt(form.sort_order) || 1,
      active: form.active,
    };

    if (editingId) {
      await supabase.from('instagram_feed').update(payload).eq('id', editingId);
      setSuccessMsg('Photo updated!');
    } else {
      await supabase.from('instagram_feed').insert(payload);
      setSuccessMsg('Photo added!');
    }

    setTimeout(() => setSuccessMsg(''), 3000);
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
    setSaving(false);
    fetchPosts();
  };

  const handleCancel = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) return (
    <div style={styles.loading}><p style={{ color: GOLD }}>Loading...</p></div>
  );

  return (
    <div style={styles.wrap}>

      {/* TOPBAR */}
      <div style={styles.topbar}>
        <h1 style={styles.topbarTitle}>
          Instagram Feed
          <span style={styles.topbarCount}>{posts.length} photos</span>
        </h1>
        <button style={styles.topbarBtn} onClick={() => { setShowForm(!showForm); setEditingId(null); setForm(emptyForm); }}>
          {showForm && !editingId ? '✕ Cancel' : '+ Add Photo'}
        </button>
      </div>

      <div style={styles.content}>

        {successMsg && <div style={styles.successBanner}>✅ {successMsg}</div>}

        {/* INFO BOX */}
        <div style={styles.infoBox}>
          <strong>How it works:</strong> Upload your Instagram photos to Supabase Storage, then paste the URL here. The first 6 active photos will show on the website in sort order. Click the Instagram URL field to link each photo back to the original post.
        </div>

        {/* FORM */}
        {showForm && (
          <div style={styles.formCard}>
            <h2 style={styles.formTitle}>{editingId ? 'Edit Photo' : 'Add New Photo'}</h2>
            <form onSubmit={handleSubmit}>

              <div style={styles.formRow}>
                <label style={styles.label}>IMAGE URL *</label>
                <input
                  style={styles.input}
                  type="text"
                  placeholder="https://... (Supabase storage URL)"
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  required
                />
                {form.image_url && (
                  <img src={form.image_url} alt="Preview" style={styles.imagePreview} />
                )}
              </div>

              <div style={styles.formGrid}>
                <div style={styles.formRow}>
                  <label style={styles.label}>CAPTION (optional)</label>
                  <input
                    style={styles.input}
                    type="text"
                    placeholder="Short caption for this photo"
                    value={form.caption}
                    onChange={(e) => setForm({ ...form, caption: e.target.value })}
                  />
                </div>
                <div style={styles.formRow}>
                  <label style={styles.label}>INSTAGRAM POST URL (optional)</label>
                  <input
                    style={styles.input}
                    type="text"
                    placeholder="https://instagram.com/p/..."
                    value={form.instagram_url}
                    onChange={(e) => setForm({ ...form, instagram_url: e.target.value })}
                  />
                  <div style={styles.fieldHint}>Links photo to original Instagram post</div>
                </div>
              </div>

              <div style={styles.formGrid}>
                <div style={styles.formRow}>
                  <label style={styles.label}>SORT ORDER</label>
                  <input
                    style={styles.input}
                    type="number"
                    min="1"
                    value={form.sort_order}
                    onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                  />
                </div>
                <div style={styles.formRow}>
                  <label style={styles.label}>STATUS</label>
                  <label style={styles.checkLabel}>
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => setForm({ ...form, active: e.target.checked })}
                      style={{ marginRight: '8px' }}
                    />
                    Active — show on website
                  </label>
                </div>
              </div>

              <div style={styles.formBtns}>
                <button type="submit" style={styles.submitBtn} disabled={saving}>
                  {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Photo'}
                </button>
                <button type="button" style={styles.cancelBtn} onClick={handleCancel}>Cancel</button>
              </div>

            </form>
          </div>
        )}

        {/* PHOTO GRID */}
        {posts.length === 0 ? (
          <div style={styles.empty}>No photos yet. Click "+ Add Photo" to add your first Instagram photo.</div>
        ) : (
          <>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Photos ({posts.length})</h2>
              <span style={styles.sectionHint}>First 6 active photos show on website</span>
            </div>
            <div style={styles.photoGrid}>
              {posts.map((post, index) => (
                <div key={post.id} style={{ ...styles.photoCard, ...(post.active ? {} : styles.photoCardInactive) }}>
                  <div style={styles.photoImgWrap}>
                    <img src={post.image_url} alt={post.caption || ''} style={styles.photoImg} />
                    {!post.active && <div style={styles.inactiveOverlay}>Hidden</div>}
                    {index < 6 && post.active && <div style={styles.visibleBadge}>Visible</div>}
                  </div>
                  {post.caption && <div style={styles.photoCaption}>{post.caption}</div>}
                  <div style={styles.photoActions}>
                    <div style={styles.reorderBtns}>
                      <button style={styles.arrowBtn} onClick={() => handleMoveUp(post, index)} disabled={index === 0}>↑</button>
                      <button style={styles.arrowBtn} onClick={() => handleMoveDown(post, index)} disabled={index === posts.length - 1}>↓</button>
                    </div>
                    <Toggle value={post.active} onChange={() => handleToggle(post)} />
                    <button style={styles.editBtn} onClick={() => handleEdit(post)}>Edit</button>
                    <button style={styles.deleteBtn} onClick={() => handleDelete(post.id)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

      </div>
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <div onClick={onChange} style={{ width: '36px', height: '20px', borderRadius: '10px', background: value ? GOLD : '#ddd', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
      <div style={{ position: 'absolute', width: '14px', height: '14px', borderRadius: '50%', background: '#fff', top: '3px', left: value ? '19px' : '3px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </div>
  );
}

const styles = {
  wrap: { display: 'flex', flexDirection: 'column', minHeight: '100vh' },
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' },
  topbar: { background: '#fff', borderBottom: '1px solid #e0e0e0', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 10 },
  topbarTitle: { fontSize: '18px', fontWeight: '500', color: '#111', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' },
  topbarCount: { fontSize: '13px', fontWeight: '400', color: '#aaa' },
  topbarBtn: { background: GOLD, color: '#111', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  content: { padding: '24px', flex: 1 },
  successBanner: { background: '#f0fff4', border: '1px solid #86efac', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', color: '#166534', fontSize: '14px' },
  infoBox: { background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', color: '#92400e', fontSize: '13px', lineHeight: 1.6 },
  formCard: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '14px', padding: '24px', marginBottom: '24px' },
  formTitle: { fontSize: '16px', fontWeight: '500', color: '#111', marginBottom: '20px' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  formRow: { marginBottom: '16px' },
  label: { display: 'block', color: '#888', fontSize: '10px', letterSpacing: '1px', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', color: '#111', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' },
  fieldHint: { color: '#bbb', fontSize: '11px', marginTop: '4px' },
  imagePreview: { width: '120px', height: '120px', objectFit: 'cover', borderRadius: '8px', marginTop: '8px', border: '1px solid #e0e0e0' },
  checkLabel: { display: 'flex', alignItems: 'center', fontSize: '14px', color: '#444', cursor: 'pointer', marginTop: '8px' },
  formBtns: { display: 'flex', gap: '12px', marginTop: '8px' },
  submitBtn: { background: GOLD, color: '#111', border: 'none', borderRadius: '10px', padding: '11px 24px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  cancelBtn: { background: 'none', color: '#888', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '11px 24px', fontSize: '14px', cursor: 'pointer' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  sectionTitle: { fontSize: '15px', fontWeight: '500', color: '#111', margin: 0 },
  sectionHint: { fontSize: '12px', color: '#aaa' },
  photoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' },
  photoCard: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '12px', overflow: 'hidden' },
  photoCardInactive: { opacity: 0.6 },
  photoImgWrap: { position: 'relative', aspectRatio: '1', overflow: 'hidden' },
  photoImg: { width: '100%', height: '100%', objectFit: 'cover' },
  inactiveOverlay: { position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '12px', fontWeight: '600' },
  visibleBadge: { position: 'absolute', top: '8px', right: '8px', background: GOLD, color: '#111', fontSize: '10px', fontWeight: '600', padding: '2px 8px', borderRadius: '20px' },
  photoCaption: { padding: '8px 12px', fontSize: '12px', color: '#666', borderBottom: '1px solid #f0f0f0' },
  photoActions: { padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px' },
  reorderBtns: { display: 'flex', gap: '2px' },
  arrowBtn: { background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '4px', width: '24px', height: '20px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, color: '#666' },
  editBtn: { background: 'none', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', color: '#444', cursor: 'pointer' },
  deleteBtn: { background: 'none', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', color: '#ff6b6b', cursor: 'pointer' },
  empty: { padding: '32px', textAlign: 'center', color: '#888', fontSize: '13px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '12px' },
};